import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import {
  MailcraftEmailBuilder,
  type MailcraftEmailBuilderHandle,
} from '../mailcraft-email-builder/mailcraft-email-builder';
import { useToast } from '@/hooks/use-toast';
import { SaveTemplateModal } from '@/modules/templates/components/save-template-modal/save-template-modal';
import { useCreateTemplate, useUpdateTemplate } from '@/modules/templates/hooks/use-templates';
import { getTemplateErrorMessage } from '@/modules/templates/utils/template-api-errors';
import { Button } from '@/components/ui-kit/button';
import { cn } from '@/lib/utils';

type CampaignEmailBuilderProps = {
  html?: string;
  name?: string;
  /** When set, “Update template” overwrites this template instead of creating a new one. */
  existingTemplateId?: string;
  existingTemplateName?: string;
  /** When false, hide the inline “Save as template” row (header owns that action). */
  showInlineActions?: boolean;
  /** Pass-through to MailCraft host sizing. */
  layout?: 'design' | 'fluid';
  /** When true, skip the built-in bordered/card wrapper — caller supplies its own box. */
  hideOuterBox?: boolean;
  onSave?: (json: string, html: string) => void;
  onContentChange?: () => void;
  onTemplateSaved?: (payload: {
    json: string;
    html: string;
    name: string;
    templateId: string;
    contentId?: string | null;
  }) => void;
};

type PendingTemplateSave = {
  name: string;
};

export type CampaignEmailBuilderHandle = {
  exportContent: () => Promise<{ json: string; html: string } | null>;
  openSaveAsTemplate: () => void;
};

/**
 * Campaign content editor — MailCraft (HTML in / HTML out), matching the design
 * prototype. Bee Pro is no longer used for new campaign content.
 */
export const CampaignEmailBuilder = forwardRef<
  CampaignEmailBuilderHandle,
  CampaignEmailBuilderProps
>(
  (
    {
      html = '',
      name = 'Draft',
      existingTemplateId = '',
      existingTemplateName = '',
      showInlineActions = true,
      layout = 'fluid',
      hideOuterBox = false,
      onSave,
      onContentChange,
      onTemplateSaved,
    },
    ref
  ) => {
    const { toast } = useToast();
    const builderRef = useRef<MailcraftEmailBuilderHandle>(null);
    const exportedHtmlRef = useRef<string | null>(null);
    const [isSaveTemplateModalOpen, setIsSaveTemplateModalOpen] = useState(false);
    const [isExportingForTemplate, setIsExportingForTemplate] = useState(false);
    const [isSavingTemplateFlow, setIsSavingTemplateFlow] = useState(false);
    const { mutateAsync: createTemplate } = useCreateTemplate();
    const { mutateAsync: updateTemplateMutation } = useUpdateTemplate();
    const hasExistingTemplate = Boolean(existingTemplateId.trim());
    const templateActionLabel = hasExistingTemplate ? 'Update template' : 'Save as template';

    const handleOpenSaveTemplateModal = () => {
      setIsExportingForTemplate(true);
      try {
        const nextHtml = builderRef.current?.exportHtml()?.trim() ?? '';
        if (!nextHtml) {
          toast({
            variant: 'destructive',
            title: 'Failed to export content',
            description: 'Could not export email HTML from MailCraft. Edit the email, then retry.',
          });
          return;
        }
        exportedHtmlRef.current = nextHtml;
        setIsSaveTemplateModalOpen(true);
      } finally {
        setIsExportingForTemplate(false);
      }
    };

    useImperativeHandle(ref, () => ({
      exportContent: async () => {
        const nextHtml = builderRef.current?.exportHtml()?.trim() ?? '';
        if (!nextHtml) return null;
        return { json: '', html: nextHtml };
      },
      openSaveAsTemplate: handleOpenSaveTemplateModal,
    }));

    const handleSaveTemplateConfirm = async ({ name: templateName }: PendingTemplateSave) => {
      setIsSavingTemplateFlow(true);
      try {
        const nextHtml =
          exportedHtmlRef.current?.trim() || builderRef.current?.exportHtml()?.trim() || '';
        if (!nextHtml) {
          throw new Error('Could not export email HTML from MailCraft.');
        }

        const result = hasExistingTemplate
          ? await updateTemplateMutation({
              templateId: existingTemplateId.trim(),
              input: { name: templateName, html: nextHtml },
            })
          : await createTemplate({
              name: templateName,
              html: nextHtml,
            });

        onTemplateSaved?.({
          json: '',
          html: nextHtml,
          name: templateName,
          templateId: result.itemId,
          contentId: result.contentId,
        });
        onSave?.('', nextHtml);
        exportedHtmlRef.current = null;
        setIsSaveTemplateModalOpen(false);
        toast({
          variant: 'success',
          title: hasExistingTemplate ? 'Template updated' : 'Template saved',
          description: hasExistingTemplate
            ? `"${templateName}" was updated.`
            : `"${templateName}" was saved to your templates.`,
        });
      } catch (error) {
        toast({
          variant: 'destructive',
          title: hasExistingTemplate ? 'Failed to update template' : 'Failed to save template',
          description: getTemplateErrorMessage(error, 'Unexpected error while saving.'),
        });
      } finally {
        setIsSavingTemplateFlow(false);
      }
    };

    return (
      <>
        <div className="min-w-0 w-full space-y-3">
          {showInlineActions ? (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-muted-foreground">
                Drag-and-drop editor powered by MailCraft (HTML in, HTML out).
              </p>
              <Button
                type="button"
                className="bg-primary-600 text-white hover:bg-primary-700"
                disabled={isExportingForTemplate}
                onClick={handleOpenSaveTemplateModal}
              >
                {isExportingForTemplate ? 'Preparing…' : templateActionLabel}
              </Button>
            </div>
          ) : null}
          {hideOuterBox ? (
            <MailcraftEmailBuilder
              ref={builderRef}
              html={html}
              name={name}
              layout={layout}
              onChange={() => onContentChange?.()}
            />
          ) : (
            <div
              className={cn(
                'w-full rounded-lg border border-border',
                layout === 'design'
                  ? 'overflow-x-auto bg-card shadow-e1'
                  : 'min-w-0 overflow-hidden'
              )}
            >
              <MailcraftEmailBuilder
                ref={builderRef}
                html={html}
                name={name}
                layout={layout}
                onChange={() => onContentChange?.()}
              />
            </div>
          )}
        </div>

        <SaveTemplateModal
          open={isSaveTemplateModalOpen}
          mode={hasExistingTemplate ? 'update' : 'create'}
          initialName={hasExistingTemplate ? existingTemplateName : ''}
          onOpenChange={(open) => {
            if (!isSavingTemplateFlow) {
              setIsSaveTemplateModalOpen(open);
              if (!open) {
                exportedHtmlRef.current = null;
              }
            }
          }}
          isSaving={isSavingTemplateFlow}
          onConfirm={(values) => {
            void handleSaveTemplateConfirm(values);
          }}
        />
      </>
    );
  }
);

CampaignEmailBuilder.displayName = 'CampaignEmailBuilder';
